import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as Square from '@square/web-sdk';
import _ from 'lodash';
import { Button } from '@/components/Elements';
import { Loader } from '@/components/Loader/Loader';
import { CashPaymentOption, Event, MultiAppointmentResponse, PaymentDetails } from '../types';
import { CreditCard } from '@/features/billing/api/upgradePlan';
import { PaymentCards } from './PaymentCards';
import { AppointmentPreview } from './AppointmentPreview';
import { PaymentOptions } from './PaymentOptions';
import { useTranslation } from 'react-i18next';
import { MultiAppointmentPreview } from './MultiAppointmentPreview';

type SquareFormProps = {
  locationId: string;
  onMakePayment: (paymentDetails?: PaymentDetails) => void;
  onError: (err?: any) => void;
  amount?: number;
  authOnly?: boolean;
  businessLocationId?: any;
  appointment?: Event;
  multiAppointmentDetails?: MultiAppointmentResponse;
  totalAmount?: number;
  onBackClick: () => void;
  paymentDetails?: PaymentDetails;
  onChange: (paymentDetails: PaymentDetails) => void;
  isLoading: boolean;
};

export type SquareFormHandle = {
  verify3DS: (storedToken?: string) => void;
};

export const MakePaymentSquare = forwardRef<SquareFormHandle, SquareFormProps>(
  (
    {
      onMakePayment,
      onError,
      locationId,
      amount,
      authOnly,
      paymentDetails,
      onChange,
      onBackClick,
      appointment,
      totalAmount,
      isLoading,
      multiAppointmentDetails,
    }: SquareFormProps,
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const [card, setCard] = useState<Square.Card>();
    const [payment, setPayment] = useState<Square.Payments>();
    const [isSquareLoading, setIsSquareLoading] = useState(true);
    const cardElRef = useRef<HTMLDivElement | null>(null);

    const verifyBuyer = async (token: string, payments?: Square.Payments) => {
      try {
        if (!payments) {
          throw new Error('Unable to initialize payment');
        }
        const verifyProps = {
          intent: 'CHARGE',
          amount: totalAmount?.toFixed(2),
          currencyCode: 'USD',
        };
        const verificationDetails: any = {
          ...verifyProps,
          billingContact: {},
        };
        const verificationResults = await payments.verifyBuyer(token, verificationDetails);
        return verificationResults?.token;
      } catch (ex) {
        onError(ex);
      }
    };

    const tokenize = useCallback(
      async (verify?: boolean) => {
        try {
          if (!card || !payment) {
            throw new Error('Unable to initialize payment');
          }
          if (paymentDetails?.data) {
            await onMakePayment();
            return;
          }
          setIsSquareLoading(true);
          const result = await card.tokenize();
          if (!result.token) throw new Error('Please enter valid card details');
          let token;
          if (verify) {
            token = await verifyBuyer(result.token, payment);
            if (!token) throw new Error('Unable to complete verification');
          }

          setIsSquareLoading(false);
          const cardDetails: CreditCard = {
            firstName: '',
            lastName: '',
            expireMonth: '',
            expireYear: '',
            number: '',
            type: '',
            isDefault: false,
            squareCardId: '',
            id: result.token || '',
          };
          //result.token, token, cardDetails
          const pd: PaymentDetails = {
            data: cardDetails,
            type: 'card',
            paymentMode: '',
            isNewCard: true,
            verificationToken: token,
          };
          await onMakePayment(pd);
        } catch (ex) {
          const code = _.get(ex, 'response.data.errors.0.code');
          if (code === 'CARD_DECLINED_VERIFICATION_REQUIRED') {
            tokenize(true);
          } else {
            onError(ex);
            setIsSquareLoading(false);
          }
        }
      },
      [card, payment, paymentDetails]
    );

    useEffect(() => {
      const initSquare = async () => {
        try {
          const payments = await Square.payments(
            process.env.REACT_APP_SQUARE_APP_ID as string,
            locationId
          );
          if (payments) {
            setPayment(payments);
          }
        } catch (e) {
          console.error('=======================================> Error initializing square: ', e);
          onError(e);
        }
        authOnly && setIsSquareLoading(false);
      };
      initSquare();
    }, []);

    useEffect(() => {
      const initCard = async () => {
        if (authOnly || !payment) {
          return;
        }
        try {
          const card = await payment.card();
          setCard(card);
          if (cardElRef && cardElRef.current) {
            await card.attach(cardElRef.current);
            setIsSquareLoading(false);
          }
          card.addEventListener('focusClassAdded', onSquareFocused);
          return () => {
            if (card) {
              card.removeEventListener('focusClassAdded', onSquareFocused);
            }
          };
        } catch (e) {
          setIsSquareLoading(false);
          console.error('=======================================> Error initializing card: ', e);
          onError(e);
        }

        setIsSquareLoading(false);
      };
      initCard();
    }, [authOnly, payment]);

    useImperativeHandle(ref, () => ({
      async verify3DS(storedToken?: string) {
        try {
          if (storedToken) {
            const token = await verifyBuyer(storedToken, payment);

            if (!token) {
              return onError(new Error('Unable to complete verification'));
            }
            //storedToken, token
            //onMakePayment();
          } else {
            await tokenize(true);
          }
        } catch (e) {
          onError(e);
        }
      },
    }));

    if (authOnly) return null;
    const onCashPaymentSelect = (cpo: CashPaymentOption) => {
      const pd: PaymentDetails = paymentDetails
        ? paymentDetails
        : { data: undefined, type: 'cash', paymentMode: '' };
      if (pd) {
        pd.data = cpo;
        pd.type = 'cash';
        onChange({ ...pd });
        setTimeout(() => {
          clearSquareFieldValues();
        }, 0);
      }
    };

    const onCardSelect = (cc: CreditCard) => {
      const pd: PaymentDetails = paymentDetails
        ? paymentDetails
        : { data: undefined, type: 'cash', paymentMode: '' };

      if (pd) {
        pd.data = cc;
        pd.type = 'card';
        onChange({ ...pd });
        setTimeout(() => {
          clearSquareFieldValues();
        }, 0);
      }
    };
    const onSquareFocused = () => {
      onChange({ data: undefined, type: 'cash', paymentMode: '' });
    };
    const clearSquareFieldValues = async () => {
      card?.clear();
    };
    return (
      <>
        <div className="make-payment-stripe">
          {multiAppointmentDetails ? (
            <MultiAppointmentPreview
              multiAppointmentDetails={multiAppointmentDetails}
              totalAmount={totalAmount}
            />
          ) : (
            appointment && (
              <AppointmentPreview selectedAppointment={appointment} totalAmount={totalAmount} />
            )
          )}
          <PaymentCards
            customerId={appointment?.petParent.id}
            paymentDetails={paymentDetails}
            onSelect={onCardSelect}
          />
          <div className="credit-card-details">
            <label>{t('New credit card')}</label>
            <div className="credit-card-form">
              {isSquareLoading && !card ? (
                <Loader text="loading..." />
              ) : (
                <div ref={cardElRef}></div>
              )}
            </div>
          </div>
          <PaymentOptions paymentDetails={paymentDetails} onSelect={onCashPaymentSelect} />
        </div>
        <div className="credit-card-form-action">
          <Button variant="nobg" size="lg" onClick={onBackClick}>
            {t('Back')}
          </Button>
          <Button size="lg" disabled={isSquareLoading || isLoading} onClick={() => tokenize()}>
            {t('Make Payment')}
          </Button>
        </div>
      </>
    );
  }
);

MakePaymentSquare.displayName = 'SquareForm'; // fix eslint warning
